import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { IEventSummary, ICourseSummary, IClub } from "@eagerdog/interfaces";

import Icon from "src/components/Icon/Icon";
import DiscoverCard from "../DiscoverCard/DiscoverCard";

import styles from "./DiscoverCarousel.module.scss";

interface IProps {
    title: string;
    items: (IEventSummary | ICourseSummary | IClub)[];
    type: "event" | "course" | "club";
    linkName: string;
    link: string;
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
}

const getVisibleCount = (): number => {
    const w = window.innerWidth;
    if (w >= 1700) return 4;
    if (w >= 1200) return 3;
    if (w >= 640)  return 2;
    return 1;
};

const DiscoverCarousel: React.FC<IProps> = (props) => {
    const { title, items, type, linkName, link, loading, hasMore, onLoadMore } = props;

    const [visibleCount, setVisibleCount] = useState<number>(getVisibleCount);

    useEffect(() => {
        const handler = () => setVisibleCount(getVisibleCount());
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);

    const placeholderCount = items.length < visibleCount
        ? visibleCount - items.length
        : 0;

    const responsive = {
        desktop: { breakpoint: { max: 3000, min: 1700 }, items: 4 },
        laptop:  { breakpoint: { max: 1700, min: 1200 }, items: 3 },
        tablet:  { breakpoint: { max: 1200, min: 640  }, items: 2 },
        mobile:  { breakpoint: { max: 640,  min: 0    }, items: 1 }
    };

    const CustomButtonGroup = ({ next, previous, ...rest }: any) => {
        const { carouselState: { currentSlide, totalItems, slidesToShow } } = rest;
        const visibleCount = slidesToShow || 4;
        const atEnd = currentSlide >= totalItems - visibleCount;

        const handleNext = () => {
            next();
            // Load the next page when the user reaches the last visible page
            if (currentSlide >= totalItems - visibleCount * 2 && hasMore && onLoadMore) {
                onLoadMore();
            }
        };

        return (
            <div className={styles.customButtonGroup}>
                <button
                    className={`${styles.caroButton} ${styles.buttonLeft}`}
                    onClick={() => previous()}
                    disabled={currentSlide === 0}
                >
                    <Icon className={styles.caroButtonIcon} type="caretLeft" />
                </button>
                <button
                    className={`${styles.caroButton} ${styles.buttonRight}`}
                    onClick={handleNext}
                    disabled={atEnd && !hasMore}
                >
                    <Icon className={styles.caroButtonIcon} type="caretRight" />
                </button>
            </div>
        );
    };

    return (
        <div className={styles.DiscoverCarousel}>
            <div className={styles.title}>{title}</div>
            <div className={styles.carouselInner}>
                {loading && (
                    <div className={styles.skeletonRow}>
                        {Array.from({ length: visibleCount }).map((_, i) => (
                            <div key={`skeleton-${i}`} className={`${styles.placeholder} ${styles.skeleton}`} />
                        ))}
                    </div>
                )}
                {!loading && (
                    <Carousel
                        swipeable={false}
                        draggable={false}
                        showDots={false}
                        responsive={responsive}
                        ssr={true}
                        infinite={false}
                        autoPlay={false}
                        autoPlaySpeed={1000}
                        keyBoardControl={false}
                        customTransition="all .500"
                        transitionDuration={500}
                        containerClass="carousel-container"
                        removeArrowOnDeviceType={["tablet", "mobile"]}
                        deviceType="desktop"
                        dotListClass="custom-dot-list-style"
                        itemClass="carousel-item-padding-40-px"
                        renderButtonGroupOutside={true}
                        customButtonGroup={<CustomButtonGroup />}
                        arrows={false}
                    >
                        {items.map((item, i) => (
                            <DiscoverCard key={item._id} type={type} data={item} index={i} />
                        ))}
                        {Array.from({ length: placeholderCount }).map((_, i) => (
                            <div key={`placeholder-${i}`} className={styles.placeholder} />
                        ))}
                    </Carousel>
                )}
            </div>
            <div className={styles.viewAll}>
                {items.length > 0 && <Link to={link}>{linkName} →</Link>}
            </div>
        </div>
    );
};

export default DiscoverCarousel;
